import type { WhatsappConversation, WhatsappMessage } from '@/services/endpoints/whatsappInbox';

export const LIST_POLLING_MS = 10_000;
export const THREAD_POLLING_MS = 5_000;
export const TEXT_MAX_LENGTH = 4096;
export const CAPTION_MAX_LENGTH = 1024;
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const DOCUMENT_MAX_BYTES = 16 * 1024 * 1024;

/** What the API accepts on POST …/media (same list as its @Consumes). */
const EXTENSION_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
const ALLOWED_MIMES = new Set(Object.values(EXTENSION_MIME));

export const ATTACHMENT_ACCEPT = Object.keys(EXTENSION_MIME)
  .map((ext) => `.${ext}`)
  .join(',');

export const isWindowOpen = (windowOpenUntil: string | null | undefined, now: number = Date.now()): boolean =>
  !!windowOpenUntil && Date.parse(windowOpenUntil) > now;

/** Conversations with at least one unread message — used for Inbox tab badges. */
export const unreadConversationCount = (conversations: WhatsappConversation[]): number =>
  conversations.filter((conversation) => conversation.unreadCount > 0).length;

/** Browsers leave File.type empty for some Office files: fall back to the extension. */
export const attachmentMime = (file: File): string | undefined => {
  if (file.type && ALLOWED_MIMES.has(file.type)) {
    return file.type;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  // own keys only: a file named "x.constructor" must not resolve to Object.prototype.constructor
  return Object.hasOwn(EXTENSION_MIME, ext) ? EXTENSION_MIME[ext] : undefined;
};

export const attachmentProblem = (file: File): 'type' | 'size' | null => {
  const mime = attachmentMime(file);
  if (!mime) {
    return 'type';
  }
  return file.size > (mime.startsWith('image/') ? IMAGE_MAX_BYTES : DOCUMENT_MAX_BYTES) ? 'size' : null;
};

export type MediaKind = 'image' | 'pdf' | 'audio' | 'video' | 'file';

export const mediaKind = (mime: string | null | undefined): MediaKind => {
  const bare = (mime ?? '').split(';')[0].trim().toLowerCase();
  if (bare.startsWith('image/')) return 'image';
  if (bare === 'application/pdf') return 'pdf';
  if (bare.startsWith('audio/')) return 'audio';
  if (bare.startsWith('video/')) return 'video';
  return 'file';
};

export const mediaUrl = (workspaceId: string, messageId: string) => `/v1/workspace/${workspaceId}/whatsapp/inbox/messages/${messageId}/media`;

/** Oldest first, as a chat reads: the API answers newest first. */
export const chronological = (messages: WhatsappMessage[] | undefined): WhatsappMessage[] => [...(messages ?? [])].reverse();

export const matchesSearch = (conversation: WhatsappConversation, term: string): boolean => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  // The phone is bare digits ("393401234567"): compare it with the digits of what was typed, so "+39 340" matches.
  // A term with no digit at all ("mario") must not match every phone, hence the guard.
  const digits = needle.replace(/\D/g, '');
  return (conversation.displayName ?? '').toLowerCase().includes(needle) || (!!digits && conversation.contactPhone.includes(digits));
};

export const contactLabel = (conversation: WhatsappConversation): string => conversation.displayName || `+${conversation.contactPhone}`;

/** Up to two initials from the profile name; '' without one (the avatar then shows a person icon). */
export const initials = (displayName: string | null | undefined): string =>
  (displayName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    // spread: an emoji is one initial, not half a surrogate pair
    .map((word) => [...word][0].toUpperCase())
    .join('');

const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;

/** How long the 24h window stays open: whole hours, minutes under the hour; null once closed. */
export const windowLeft = (windowOpenUntil: string | null | undefined, now: number = Date.now()): { unit: 'hours' | 'minutes'; count: number } | null => {
  const left = windowOpenUntil ? Date.parse(windowOpenUntil) - now : 0;
  if (!(left > 0)) return null;
  return left >= HOUR_MS ? { unit: 'hours', count: Math.floor(left / HOUR_MS) } : { unit: 'minutes', count: Math.ceil(left / MINUTE_MS) };
};

// Calendar days in the operator's timezone; round, not floor: a DST day lasts 23 or 25 hours.
const midnight = (time: number) => new Date(time).setHours(0, 0, 0, 0);
const daysAgo = (iso: string, now: number) => Math.round((midnight(now) - midnight(Date.parse(iso))) / (24 * HOUR_MS));

export const sameDay = (a: string, b: string): boolean => midnight(Date.parse(a)) === midnight(Date.parse(b));

/** The day separator of a thread: today, yesterday, else the date (with the year only when it is not this one). */
export const dayLabel = (iso: string, labels: { today: string; yesterday: string }, now: number = Date.now()): string => {
  const days = daysAgo(iso, now);
  if (days === 0) return labels.today;
  if (days === 1) return labels.yesterday;
  const date = new Date(iso);
  return date.toLocaleDateString([], { day: 'numeric', month: 'long', year: date.getFullYear() === new Date(now).getFullYear() ? undefined : 'numeric' });
};

export const clockTime = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** The conversation list time: a clock today, "yesterday", then a short date. */
export const listTime = (iso: string | null | undefined, yesterday: string, now: number = Date.now()): string => {
  if (!iso) return '';
  const days = daysAgo(iso, now);
  if (days === 0) return clockTime(iso);
  return days === 1 ? yesterday : new Date(iso).toLocaleDateString([], { dateStyle: 'short' });
};

/** The template body as the customer will read it: typed variables in place of {{1}}, {{2}}…, an empty one keeps its placeholder. */
export const fillTemplate = (body: string, params: string[]): string =>
  body.replace(/\{\{(\d+)\}\}/g, (placeholder, index: string) => {
    const value = params[Number(index) - 1];
    return value?.trim() ? value : placeholder;
  });
